import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { Plus } from "lucide-react";
import { api } from "../../api";
import { Button } from "../../components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../components/ui/table";
import { StatusPill } from "../../components/common/StatusPill";
import { ErrorState } from "../../components/common/ErrorState";
import { SortControl } from "../../components/common/SortControl";
import { useSortPersistence } from "../../hooks/useSortPersistence";
import { ProvisionGymDialog } from "../../components/control/ProvisionGymDialog";
import { CONTROL } from "../../constants/testIds";
import { fadeUp } from "../../lib/motion";

const STATUS_FILTERS = [
  { value: "all", label: "All statuses" },
  { value: "active", label: "Active" },
  { value: "trial", label: "Trial" },
  { value: "grace", label: "Grace period" },
  { value: "read_only", label: "Suspended (read-only)" },
  { value: "suspended", label: "Suspended (blocked)" },
  { value: "deleted", label: "Deleted" },
];
const SORT_FIELDS = [
  { value: "id", label: "Newest first", type: "date" },
  { value: "name", label: "Name (A–Z)", type: "alpha" },
  { value: "branches", label: "Branches", type: "numeric" },
];

// UX-CP-01 §2 SCR-P08-03 "Gyms list" — filterable table over the real `/control/gyms` join.
// DEFECT-D-22: `?status=` in the URL is honored on load — the platform dashboard's KPI
// tiles/chart segments deep-link straight into this exact filtered view, never a dead end.
export default function GymsList() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState(searchParams.get("status") || "all");
  const [provisionOpen, setProvisionOpen] = useState(false);
  const navigate = useNavigate();
  const reducedMotion = useReducedMotion();
  const { sortBy, sortDir, setSort } = useSortPersistence("control-gyms-list", "id", "desc");

  const gymsQ = useQuery({
    queryKey: ["control-gyms"],
    queryFn: async () => (await api.get("/control/gyms")).data.gyms,
  });

  const rows = useMemo(() => {
    if (!gymsQ.data) return [];
    let filtered = status === "all" ? gymsQ.data : gymsQ.data.filter((g) => g.status === status);
    filtered = [...filtered];
    if (sortBy === "name") filtered.sort((a, b) => a.display_name.localeCompare(b.display_name) * (sortDir === "asc" ? 1 : -1));
    else if (sortBy === "branches") filtered.sort((a, b) => (a.branch_count - b.branch_count) * (sortDir === "asc" ? 1 : -1));
    else if (sortDir === "asc") filtered.reverse();
    return filtered;
  }, [gymsQ.data, status, sortBy, sortDir]);

  if (gymsQ.isError) return <ErrorState error={gymsQ.error} onRetry={() => gymsQ.refetch()} title="Couldn't load the gyms list" />;

  return (
    <div data-testid={CONTROL.gymsPage} className="space-y-6 max-w-6xl pb-8">
      <motion.div {...fadeUp(reducedMotion)} className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold" style={{ color: "var(--scarlet-text)", letterSpacing: "-0.02em" }}>Gyms</h1>
          <p className="text-base mt-1" style={{ color: "var(--scarlet-text-secondary)" }}>Every gym tenant on the platform.</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid={CONTROL.statusFilter} className="w-[200px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {STATUS_FILTERS.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <SortControl fields={SORT_FIELDS} sortBy={sortBy} sortDir={sortDir} onChange={setSort} testId={CONTROL.gymsSortControl} />
          <Button data-testid={CONTROL.provisionButton} onClick={() => setProvisionOpen(true)} className="text-white gap-2" style={{ background: "var(--scarlet-accent)" }}>
            <Plus size={16} /> Provision gym
          </Button>
        </div>
      </motion.div>

      <motion.div {...fadeUp(reducedMotion, 0.05)} className="solid-surface rounded-[14px] overflow-hidden">
        <Table data-testid={CONTROL.gymsTable}>
          <TableHeader>
            <TableRow>
              <TableHead>Gym</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Plan</TableHead>
              <TableHead>Branches</TableHead>
              <TableHead>Seats</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {gymsQ.isLoading && (
              <TableRow><TableCell colSpan={5} className="text-center py-8 text-sm" style={{ color: "var(--scarlet-text-secondary)" }}>Loading gyms…</TableCell></TableRow>
            )}
            {!gymsQ.isLoading && rows.length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center py-8 text-sm" style={{ color: "var(--scarlet-text-secondary)" }}>No gyms match this filter.</TableCell></TableRow>
            )}
            {rows.map((g) => (
              <TableRow
                key={g.organization_id}
                data-testid={CONTROL.gymRow(g.organization_id)}
                className="cursor-pointer scarlet-focusable"
                tabIndex={0}
                onClick={() => navigate(`/kairoo/gyms/${g.organization_id}`)}
              >
                <TableCell className="font-medium" style={{ color: "var(--scarlet-text)" }}>{g.display_name}</TableCell>
                <TableCell><StatusPill status={g.status} /></TableCell>
                <TableCell className="capitalize" style={{ color: "var(--scarlet-text-secondary)" }}>{g.plan_tier || "—"}</TableCell>
                <TableCell style={{ color: "var(--scarlet-text-secondary)" }}>{g.branch_count}</TableCell>
                <TableCell style={{ color: "var(--scarlet-text-secondary)" }}>{g.seats_used} / {g.seat_limit ?? "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </motion.div>

      <ProvisionGymDialog open={provisionOpen} onOpenChange={setProvisionOpen} />
    </div>
  );
}
